import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { useSportNews } from "@/hooks/useSportNews";
import { getDefaultThumbnail } from "@/lib/strapi";

const PAGE_SIZE = 12;

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function SportNewsPage() {
  const {
    articles,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useSportNews(PAGE_SIZE);
  const fallbackThumbnail = getDefaultThumbnail();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  const getArticlePath = (article) => `/sport-news/${article?.slug || article?.id}`;

  return (
    <div data-testid="sport-news-page" className="min-h-screen">
      <Header />

      <PageBanner
        eyebrow="Sports Feed"
        title="Latest Sports News"
        description="Live RSS-powered swimming and sports headlines aggregated from trusted sources."
        image="https://images.unsplash.com/photo-1600965962102-9d260a71890d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
      />

      <section className="bg-amber-pale py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {isLoading && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                Loading sport news...
              </div>
            )}

            {!!error && !isLoading && articles.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-red-600">
                Unable to load sport news right now. Please try again shortly.
              </div>
            )}

            {!isLoading && !error && !articles.length && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                No sport news articles found.
              </div>
            )}

            {!isLoading && articles.map((article) => (
              <motion.div key={article.id} variants={itemVariants}>
                <Link
                  // to={getArticlePath(article)}
                  to={article.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`sport-news-article-${article.id}`}
                  className="group bg-white rounded-sm overflow-hidden border border-near-black/5 hover:-translate-y-1 transition-transform duration-200 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={article.image}
                      alt={article.title}
                      loading="lazy"
                      onError={handleImageError}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-solar-orange text-near-black font-inter font-semibold text-[11px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm">
                      {article.category}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-warm-gray mb-3">
                      <Clock size={12} />
                      <span className="font-inter font-light text-xs">{article.date}</span>
                    </div>
                    <h3 className="font-inter font-semibold text-lg text-near-black tracking-tight leading-snug mb-2 group-hover:text-amber-dusk transition-colors duration-150">
                      {article.title}
                    </h3>
                    <p className="font-inter font-normal text-sm text-warm-gray leading-relaxed flex-1 line-clamp-3">
                      {article.teaser}
                    </p>
                    <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk mt-4 group-hover:gap-2 transition-all duration-150">
                      Read more
                      <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}

            {!isLoading && articles.length > 0 && (
              <div className="md:col-span-2 lg:col-span-3 mt-4 flex flex-col items-center gap-4">
                {!!error && (
                  <p className="font-inter text-sm text-red-600">
                    Unable to load more sport news right now. Please try again shortly.
                  </p>
                )}

                {hasMore && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full bg-near-black px-6 py-3 font-inter text-sm text-white transition-colors duration-150 hover:bg-amber-dusk disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading more sport news..." : "Load More Sport News"}
                  </button>
                )}

                {!hasMore && !isLoadingMore && (
                  <p className="font-inter text-sm text-warm-gray">
                    You’ve reached the end of the sport news.
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
